import {
  CourseMappingAttributes,
  CourseMappingGroup,
  CourseMappingVisibleRow,
} from '../../interfaces/CourseMappingInterface';
import { CourseMappingPendingChanges, getEffectiveRowAttributes } from '../../utils/courseMappingChanges';
import { formatCredits, formatLanguageLevel } from './courseMappingCellFormatters';

export const COURSE_MAPPING_GROUPS_PAGE_SIZE = 30;

export interface CourseMappingRowValues {
  name: string;
  code: string;
  schoolName: string;
  academicPeriod: string;
  subjectArea: string;
  subjectSubcategory: string;
  numberOfCredits: string;
  languageCategory: string;
  languageLevel: string;
  isCollegePreparatory: boolean;
  isHonors: boolean;
  isAp: boolean;
  isIb: boolean;
  isAice: boolean;
  isDualEnrollment: boolean;
  isCte: boolean;
  isLabScience: boolean;
}

const attributesToRowValues = (
  group: CourseMappingGroup,
  attributes: CourseMappingAttributes,
  schoolName: string,
  academicPeriod: string
): CourseMappingRowValues => ({
  name: group.name,
  code: group.code,
  schoolName,
  academicPeriod,
  subjectArea: attributes.subject_area?.name ?? ' ',
  subjectSubcategory: attributes.subject_area?.subcategory ?? ' ',
  numberOfCredits: formatCredits(attributes.number_of_credits) ?? ' ',
  languageCategory: attributes.language_category ?? ' ',
  languageLevel: formatLanguageLevel(attributes.language_level) ?? ' ',
  isCollegePreparatory: Boolean(attributes.is_college_preparatory),
  isHonors: Boolean(attributes.is_honors),
  isAp: Boolean(attributes.is_ap),
  isIb: Boolean(attributes.is_ib),
  isAice: Boolean(attributes.is_aice),
  isDualEnrollment: Boolean(attributes.is_dual_enrollment),
  isCte: Boolean(attributes.is_cte),
  isLabScience: Boolean(attributes.is_lab_science),
});

export const getRowCourseId = (row: CourseMappingVisibleRow): string =>
  row.rowKind === 'parent' ? row.group.parentOffering.courseId : row.child.courseId;

export const getParentRowValues = (
  group: CourseMappingGroup,
  pendingChanges: CourseMappingPendingChanges
): CourseMappingRowValues =>
  attributesToRowValues(
    group,
    getEffectiveRowAttributes(group, group.parentOffering.courseId, pendingChanges),
    group.parentOffering.schoolName,
    group.parentOffering.academicPeriod
  );

export const getChildRowValues = (
  group: CourseMappingGroup,
  child: CourseMappingGroup['children'][number],
  pendingChanges: CourseMappingPendingChanges
) =>
  attributesToRowValues(
    group,
    getEffectiveRowAttributes(group, child.courseId, pendingChanges),
    child.schoolName,
    child.academicPeriod
  );

export const canExpandGroup = (group: CourseMappingGroup) => group.children.length > 0;

const normalizeCellValue = (value: string | boolean): string => {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  const trimmed = value.trim();
  return trimmed === '' ? '' : trimmed;
};

export const getGroupColumnValues = (
  group: CourseMappingGroup,
  columnKey: keyof CourseMappingRowValues,
  pendingChanges: CourseMappingPendingChanges
): Array<CourseMappingRowValues[keyof CourseMappingRowValues]> => {
  const parentValues = getParentRowValues(group, pendingChanges);

  return [
    parentValues[columnKey],
    ...group.children.map((child) => getChildRowValues(group, child, pendingChanges)[columnKey]),
  ];
};

export const getOtherDistinctValuesCount = (
  group: CourseMappingGroup,
  columnKey: keyof CourseMappingRowValues,
  displayedValue: string | boolean,
  pendingChanges: CourseMappingPendingChanges
): number => {
  if (!canExpandGroup(group)) {
    return 0;
  }

  const normalizedDisplayed = normalizeCellValue(displayedValue);
  const uniqueOtherValues = new Set<string>();

  getGroupColumnValues(group, columnKey, pendingChanges).forEach((value) => {
    const normalizedValue = normalizeCellValue(value);
    if (normalizedValue !== normalizedDisplayed) {
      uniqueOtherValues.add(normalizedValue);
    }
  });

  return uniqueOtherValues.size;
};

const assignGroupRowNumbers = (groups: CourseMappingGroup[], startingRowNumber: number) => {
  let nextRowNumber = startingRowNumber;

  return groups.map((group) => {
    const parentRowNumber = nextRowNumber++;
    const childRowNumbers = new Map<string, number>();

    if (canExpandGroup(group)) {
      group.children.forEach((child) => {
        childRowNumbers.set(child.id, nextRowNumber++);
      });
    }

    return { group, parentRowNumber, childRowNumbers };
  });
};

export const buildVisibleRows = (
  groups: CourseMappingGroup[],
  expandedGroupIds: Set<string>,
  startingRowNumber = 1
): CourseMappingVisibleRow[] =>
  assignGroupRowNumbers(groups, startingRowNumber).flatMap(({ group, parentRowNumber, childRowNumbers }) => {
    const parentRow: CourseMappingVisibleRow = {
      rowKind: 'parent',
      group,
      rowNumber: parentRowNumber,
    };

    if (!expandedGroupIds.has(group.id) || !canExpandGroup(group)) {
      return [parentRow];
    }

    const childRows: CourseMappingVisibleRow[] = group.children.map((child) => ({
      rowKind: 'child',
      group,
      child,
      rowNumber: childRowNumbers.get(child.id)!,
    }));

    return [parentRow, ...childRows];
  });
